import { Box, FormControl, FormControlLabel } from '@mui/material';
import {
  NestedFieldsSelector,
  NestedFieldsField,
} from '../../common/fieldsSelectors/NestedFieldsSelector';
import { useCallback } from 'react';
import FieldLabel from '../../common/FieldLabel';
import { Button, Checkbox, Typography, colors } from '@sweep-io/sweep-design';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import { SlackNewMessageEditorLexical as TeamsMessageEditor } from '../slack/SlackNewMessageEditorLexical';
import { TeamsRecipientInfo, TeamsRecipientStruct, TeamsRecipientType } from '../teams/types';
import { useRunOnce } from '../../common/useRunOnce';
import { SlackAdditionalFieldsTable } from '../slack/SlackAdditionalFieldsTable';
import { TeamsTeamSelector } from './TeamsTeamSelector';
import { TeamsMembersSelector } from './TeamsMembersSelector';
import {
  selectTeamsIntegrationStatus,
  TeamsConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { TeamsNotConnected } from './TeamsNotConnected';
import { useSelector } from 'react-redux';
import { SfdcDisplayType } from '../../../types/fields';

interface TeamsDealRoomActionProps {
  readonly?: boolean;
  action: TeamsDealRoomAutomationAction;
  crmOrgId: string;
  onChange: (teamsDealRoomAutomationAction: TeamsDealRoomAutomationAction) => any;
  objectName: string;
}

export const TeamsDealRoomAction = ({
  readonly,
  action,
  crmOrgId,
  objectName,
  onChange,
}: TeamsDealRoomActionProps) => {
  const { getEnrichedNestedPath } = useSweepFieldsInfo();
  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  readonly = readonly || teamsStatus !== TeamsConnectionStatus.Connected;

  useRunOnce(async () => {
    updateOnChange({
      fields: [...(action.actionParams?.fields || [])],
      channelName: {
        ...action.actionParams?.channelName,
        type: action.actionParams?.channelName?.type ?? 'Public',
      },
    });
  });

  const updateOnChange = useCallback(
    (updateProps: Partial<AutomationTeamsDealRoomActionParams>) => {
      onChange({
        ...action,
        actionParams: {
          ...action.actionParams,
          ...updateProps,
        },
      });
    },
    [action, onChange],
  );

  const _onMembersChange = useCallback(
    (newRecipients: TeamsRecipientInfo[]) => {
      const newItems = newRecipients.map((el: TeamsRecipientInfo) => {
        if (el.type === TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD) {
          return {
            ...el,
            type: TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
            value: {
              fieldIds: Array.isArray(el.id) ? el.id : [el.id],
            },
            label: el.label,
          };
        } else if (el.type === TeamsRecipientType.CHANNEL_STORED_IN_SALESFORCE_FIELD) {
          return {
            ...el,
            type: TeamsRecipientType.CHANNEL_STORED_IN_SALESFORCE_FIELD,
            value: {
              fieldIds: Array.isArray(el.id) ? el.id : [el.id],
            },
            label: el.label,
          };
        } else {
          return {
            ...el,
            value: el.id,
            label: el.label,
          };
        }
      });

      updateOnChange({ members: newItems as TeamsRecipientStruct[] });
    },
    [updateOnChange],
  );

  return (
    <>
      {teamsStatus !== TeamsConnectionStatus.Connected && (
        <Box sx={{ marginTop: '32px' }}>
          <TeamsNotConnected teamsStatus={teamsStatus} />
        </Box>
      )}

      {(teamsStatus === TeamsConnectionStatus.Connected ||
        action?.actionParams?.channelName?.name) && (
        <Box
          sx={{
            background: colors.grey[100],
            padding: '24px 24px 19px',
            borderRadius: '8px',
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TeamsMessageEditor
            customOnKeyDown={(event) => {
              if (!(event.key.match(/[a-z0-9,]/) || event.key === '-' || event.key === '_')) {
                event.preventDefault();
              }
            }}
            infoTooltipTitle={
              'Channel names may only contain lowercase letters, numbers, hyphens, and underscores, and must be 80 characters or less.'
            }
            placeholder="Set channel name. E.g., dealroom-{opportunity.name}"
            isRequired={true}
            hideEmoji={true}
            label="Teams channel name"
            readonly={readonly}
            messageStruct={
              action.actionParams?.channelName?.name ?? { template: '', variables: [] }
            }
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({
                channelName: {
                  ...action.actionParams?.channelName,
                  name: newMsg,
                },
              });
            }}
            objectName={objectName}
          />

          <Box sx={{ mb: '20px', mt: '-15px' }}>
            <FormControlLabel
              label={<Typography variant="body">Make private</Typography>}
              control={
                <Checkbox
                  checked={action.actionParams?.channelName?.type === 'Private'}
                  onChange={(value) => {
                    const isChecked = value.target.checked;
                    updateOnChange({
                      channelName: {
                        ...action.actionParams?.channelName,
                        type: isChecked ? 'Private' : 'Public',
                      },
                    });
                  }}
                />
              }
            />
          </Box>

          <FormControl
            sx={{
              width: '100%',
              marginBottom: '20px',
              maxWidth: '1148px',
            }}
          >
            <Box
              sx={{
                marginBottom: '3px',
                '& div': {
                  color: colors.grey[800],
                  fontSize: '12px',
                  fontWeight: '400',
                },
              }}
            >
              <FieldLabel label={'Select Team'} markLabelAsRequired={true} />
            </Box>

            <TeamsTeamSelector
              onChange={(teamId) => {
                updateOnChange({
                  teamId,
                  members: [],
                });
              }}
              readonly={readonly}
              teamId={action.actionParams?.teamId}
            />
          </FormControl>

          <FormControl
            sx={{
              width: '100%',
              marginBottom: '20px',
              maxWidth: '1148px',
            }}
          >
            <Box
              sx={{
                marginBottom: '3px',
                '& div': {
                  color: colors.grey[800],
                  fontSize: '12px',
                  fontWeight: '400',
                },
              }}
            >
              <FieldLabel label={'Teams channel members'} markLabelAsRequired={true} />
            </Box>

            <TeamsMembersSelector
              teamId={action.actionParams?.teamId}
              selectedMembers={action.actionParams?.members || []}
              onChange={_onMembersChange}
              readonly={!!readonly}
              customButtonText="Select recipients"
              crmOrgId={crmOrgId}
              objectName={objectName}
            />
          </FormControl>

          <TeamsMessageEditor
            placeholder="Type message body..."
            isRequired={false}
            label="Channel welcome message"
            readonly={readonly}
            messageStruct={action.actionParams?.welcomeMessage ?? { template: '', variables: [] }}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({ welcomeMessage: newMsg });
            }}
            objectName={objectName}
          />

          <SlackAdditionalFieldsTable
            readonly={readonly}
            fields={action.actionParams?.fields}
            updateOnChange={(fields) => {
              updateOnChange({ fields });
            }}
            crmOrgId={crmOrgId}
            objectName={objectName}
          />

          <Box>
            <Box sx={{ marginBottom: '3px' }}>
              <FieldLabel label={'Save Teams channel ID on the triggering record (Optional)'} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: '4px',
                  background: colors.white,
                  maxWidth: '500px',
                  width: 'fit-content',
                }}
              >
                <NestedFieldsSelector
                  filterBy={(field: SweepField) => field?.sfDisplayType === SfdcDisplayType.STRING}
                  disableLookupItemsResolve
                  readonly={readonly}
                  crmOrgId={crmOrgId}
                  objectType={objectName}
                  placeholder="Choose field"
                  nestedPath={getEnrichedNestedPath(action?.actionParams?.channelIdField?.fieldIds)}
                  onChange={(sweepField: NestedFieldsField) => {
                    const newItem = {
                      fieldIds: sweepField.fieldIds,
                    };
                    updateOnChange({ channelIdField: newItem });
                  }}
                />
              </Box>
              {action?.actionParams?.channelIdField && (
                <Button
                  onClick={() => {
                    updateOnChange({ channelIdField: undefined });
                  }}
                  variant="flat-blue"
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};
